import axios, { AxiosResponse, AxiosError } from 'axios';

import { APIBASEURL, DEBUG } from '@/main';
import {
  PolarisResponse,
  KazooUser,
  CheckoutForm,
  NumberInfo,
  RegisterAuth0Data,
  Auth0User,
  Location,
  CheckoutVerbose,
} from '@/shared/models/api.model';

export const HTTP = axios.create({
  baseURL: APIBASEURL,
  withCredentials: true,
});

/**
 * Is used to return consistent responses from API calls
 * @param promise - axios request
 */
export const wrapper = <T>(promise: any): { data: T; error: { message: string; status: number } } =>
  promise
    .then((response: AxiosResponse<PolarisResponse>) => {
      if (response.data.status === 'success') {
        const error = '';
        const data = response.data.data;
        if (DEBUG) {
          console.log(data);
        }
        return { data, error };
      } else {
        const error = { message: response.data.message || '', status: 500 };
        const data = response.data.data || {};
        console.error(error);
        return { data, error };
      }
    })
    .catch((error: AxiosError) => {
      if (axios.isCancel(error)) {
        console.warn('This means an Axios request was cancelled.');
      }
      const data = {};
      const response = error.response!; // FIXME: The response may not exist.
      console.error(response);

      return { data, error: { message: response.data, status: response.status } };
    });

export const checkDomain = async <T>(
  email: string | null,
  invited: boolean,
  polarisAccountId: string | null,
): Promise<{ error: { message: string; status: number }; data: T }> => {
  return wrapper<T>(HTTP.post(`/api/suo/check_domain`, { email, invited, polarisAccountId }));
};
export const reserveSelectedNumber = async <T>(
  numberToReserve: string,
): Promise<{ error: { message: string; status: number }; data: T }> => {
  return wrapper<T>(HTTP.post(`/api/suo/reserve_number`, { numberToReserve }));
};

// Auth0
export const registerAuth0 = async <T>(
  email: string,
  firstName: string,
  lastName: string,
  password: string,
  invited: boolean,
  existingUser: boolean | null,
): Promise<{ error: { message: string; status: number }; data: RegisterAuth0Data }> => {
  return wrapper<RegisterAuth0Data>(
    HTTP.post(`/api/suo/register_auth0`, {
      email,
      password,
      firstName,
      lastName,
      invited,
      existingUser,
    }),
  );
};

export const sendAdminRequestEmail = async <T>(
  ownerId: string,
  polarisAdminDetails: any,
  invitedFName: string,
  invitedLName: string,
  requesterEmail: string,
): Promise<{ error: { message: string; status: number }; data: T }> => {
  return wrapper<T>(
    HTTP.post(`/api/team/request_access`, {
      ownerId,
      polarisAdminDetails,
      invitedFName,
      invitedLName,
      requesterEmail,
    }),
  );
};

// NOT USED DELETE
// export const getAuth0AccessToken = async <T>(
//   auth0UserID: string,
//   auth0EncryptedPassword: string,
// ): Promise<{ error: { message: string; status: number }; data: T }> => {
//   return wrapper<T>(HTTP.post(`/api/suo/get_auth0_access_token`, { auth0UserID, auth0EncryptedPassword }));
// };

export const auth0PasswordReset = async <T>(
  email: string,
  redirect: string,
): Promise<{ error: { message: string; status: number }; data: T }> => {
  return wrapper<T>(HTTP.post(`/api/jobs/auth0_password_reset`, { email, redirect }));
};
export const resendAuth0VerificationEmail = async <T>(
  email: string,
): Promise<{ error: { message: string; status: number }; data: T }> => {
  return wrapper<T>(HTTP.post(`/api/suo/resend_auth0_verification_email`, { email }));
};

// Kazoo
export const registerKazoo = async <T>(
  checkoutForm: CheckoutForm,
  timezone: string | null,
  polarisAccountId: string | null,
): Promise<{ error: { message: string; status: number }; data: T }> => {
  return wrapper<T>(
    HTTP.post(`/api/suo/register_kazoo`, {
      checkoutForm,
      timezone,
      polarisAccountId,
    }),
  );
};

// Get number from tele co
export const getNumbers = async <T>(
  query: any,
  queryType: string,
): Promise<{ error: { message: string; status: number }; data: T }> => {
  return wrapper<T>(HTTP.post(`/api/suo/get_numbers`, { query, queryType }));
};

export const purchaseNumber = async <T>(
  numberInfo: NumberInfo,
  email: string,
  cancel: any,
): Promise<{ error: { message: string; status: number }; data: T }> => {
  return wrapper<T>(HTTP.post(`/api/suo/purchase_number`, { numberInfo, email }, { cancelToken: cancel }));
};

// Stripe

export const registerStripe = async <T>(
  token: any,
  checkoutForm: CheckoutForm,
  phoneNumber: string,
  cancel: any,
): Promise<{ error: { message: string; status: number }; data: T }> => {
  return wrapper<T>(
    HTTP.post(
      `/api/stripe/register_stripe`,
      {
        token,
        checkoutForm,
        phoneNumber,
      },
      { cancelToken: cancel },
    ),
  );
};

export const checkCoupon = async <T>(): Promise<{ error: { message: string; status: number }; data: T }> => {
  return wrapper<T>(HTTP.get(`/api/stripe/check_coupon`));
};

export const getPaymentPlans = async <T>(): Promise<{ error: { message: string; status: number }; data: T }> => {
  return wrapper<T>(HTTP.get(`/api/stripe/get_payment_plans`));
};

export const getTaxRates = async <T>(): Promise<{ error: { message: string; status: number }; data: T }> => {
  return wrapper<T>(HTTP.get(`/api/stripe/get_tax_rates`));
};
export const getLocation = async <T>(ip: string): Promise<{ error: { message: string; status: number }; data: T }> => {
  return wrapper<T>(HTTP.get(`/api/suo/get_location/${ip}`));
};

export const checkToken = async <T>(
  token: string,
): Promise<{ error: { message: string; status: number }; data: T }> => {
  return wrapper<T>(HTTP.get(`/api/team/checkInviteToken`, { params: { token } }));
};
export const getAdminDetails = async <T>(
  polarisAccountId: string,
): Promise<{ error: { message: string; status: number }; data: T }> => {
  return wrapper<T>(HTTP.get(`/api/team/getAdminDetails`, { params: { polarisAccountId } }));
};

export const createSubscription = async <T>(
  stripeUserID: string,
  selectedPlan: string,
  couponID: string | null,
  customerTaxIDs: T[],
  cancel: any,
): Promise<{ error: { message: string; status: number }; data: T }> => {
  return wrapper<T>(
    HTTP.post(
      '/api/stripe/create_subscription',
      {
        stripeUserID,
        selectedPlan,
        couponID,
        customerTaxIDs,
      },
      { cancelToken: cancel },
    ),
  );
};

export const invitedUserSubscription = async <T>(
  token: string,
  cancel: any,
): Promise<{ error: { message: string; status: number }; data: T }> => {
  return wrapper<T>(HTTP.post(`/api/stripe/invited_user_subscription`, { token }, { cancelToken: cancel }));
};

// Misc.
export const sendCheckoutEmailConfirmation = async <T>(
  numberPurchased: string,
  email: string,
  name: string,
  checkoutVerbose: CheckoutVerbose,
): Promise<{ error: { message: string; status: number }; data: T }> => {
  return wrapper<T>(
    HTTP.post(`/api/suo/send_checkout_confirmation_email`, { numberPurchased, email, name, checkoutVerbose }),
  );
};
export const deleteTripodToken = async <T>(
  inviteToken: string,
): Promise<{ error: { message: string; status: number }; data: T }> => {
  return wrapper<T>(HTTP.post(`/api/suo/delete_tripod_token`, { inviteToken }));
};

export const postCheckoutActions = async <T>(
  purchasedNumber: string,
  checkoutForm: CheckoutForm,
  domain: string | null,
  signUpEmail: string,
  invited: boolean,
  polarisAccountId: string,
): Promise<{ error: { message: string; status: number }; data: T }> => {
  return wrapper<T>(
    HTTP.post(`/api/suo/post_checkout_actions`, {
      purchasedNumber,
      checkoutForm,
      domain,
      signUpEmail,
      invited,
      polarisAccountId,
    }),
  );
};

export const startACActions = async <T>(
  email: string,
  location: Location,
  fName: string,
  lName: string,
): Promise<{ error: { message: string; status: number }; data: T }> => {
  return wrapper<T>(HTTP.post(`/api/suo/start_ac_actions`, { email, location, fName, lName }));
};

export const handleCheckoutDB = async <T>(
  stripeUserAPIResponse: any | null,
  stripeSubscriptionAPIResponse: any | null,
  kazooAPIResponse: any,
  numberInfo: NumberInfo,
  auth0User: Auth0User,
  invited: boolean,
  timezone: string,

  cancel: any,
): Promise<{ error: { message: string; status: number }; data: T }> => {
  return wrapper<T>(
    HTTP.post(
      `/api/suo/handle_checkout_db`,
      {
        stripeUserAPIResponse,
        stripeSubscriptionAPIResponse,
        kazooAPIResponse,
        numberInfo,
        auth0User,
        invited,
        timezone,
      },
      { cancelToken: cancel },
    ),
  );
};
